import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { trackClick } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/promozioni", label: "Promozioni" },
  { to: "/bookmaker", label: "Bookmaker" },
  { to: "/come-funziona", label: "Come funziona" },
  { to: "/community", label: "Community" },
  { to: "/lavora-con-noi", label: "Lavora con noi" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <img
            src={settings.logo_url}
            alt="VINKO"
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  isActive
                    ? "text-yellow-400"
                    : "text-zinc-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={settings.telegram_bot_url}
            target="_blank"
            rel="noopener"
            onClick={() => trackClick("telegram_bot")}
            data-testid="navbar-cta-bot"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-4 py-2 transition-transform duration-200 hover:scale-105 yellow-glow"
          >
            ACCEDI AL BOT
          </a>
          <button
            aria-label="Menu"
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-white/10 text-white"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base font-medium ${
                    isActive
                      ? "text-yellow-400 bg-white/5"
                      : "text-zinc-200 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={settings.telegram_bot_url}
              target="_blank"
              rel="noopener"
              onClick={() => trackClick("telegram_bot")}
              data-testid="navbar-mobile-cta-bot"
              className="mt-2 inline-flex justify-center items-center rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-4 py-3 transition-transform duration-200 hover:scale-[1.02]"
            >
              ACCEDI AL BOT
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
